import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'

import DisplayError from '@/components/DisplayError'
import useMeliLogin from '@/hooks/useMeliLogin'
import { useAuth } from '@/hooks/useAuth'

const MeliRedirected: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { meliExchangeCode, errorMeliExchangeCode } = useMeliLogin()
  const { userData } = useAuth()
  const navigate = useNavigate()
  const [processingStatus, setProcessingStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    const fetchAsync = async () => {
      const code = searchParams.get('code')
      const stateParam = searchParams.get('state')

      // Try to extract profile from state parameter
      // Supports both formats:
      // - New format: plain profile ID (e.g., "69614dcd4537f1046999ace0")
      // - Legacy format: JSON string (e.g., '{"profile":"69614dcd4537f1046999ace0"}')
      let profileFromState: string | null = null

      if (stateParam) {
        const decodedState = decodeURIComponent(stateParam)

        // Check if it's JSON format (starts with '{') or plain ID
        if (decodedState.startsWith('{')) {
          // Legacy JSON format
          try {
            const stateObj = JSON.parse(decodedState)
            profileFromState = stateObj.profile || null

            if (profileFromState) {
              console.log('Found profile ID in OAuth state parameter (JSON format):', profileFromState)
            }
          } catch (e) {
            console.error('Failed to parse state parameter as JSON:', e)
          }
        } else {
          // New plain ID format
          profileFromState = decodedState
          console.log('Found profile ID in OAuth state parameter (plain format):', profileFromState)
        }
      }

      // Check for profile data existence
      let hasProfileData = false
      let profileId: string | null = null

      // Check userData first
      if (userData?.profile) {
        hasProfileData = true
        profileId = userData.profile._id
        console.log('Using profile from userData for MercadoLibre authentication')
      } else if (profileFromState) {
        // Check state parameter next (new approach)
        hasProfileData = true
        profileId = profileFromState
        console.log('Using profile from state parameter for MercadoLibre authentication')

        // Temporarily store in sessionStorage for the exchange code call
        sessionStorage.setItem('meliProfileId', profileId)
      } else if (sessionStorage.getItem('meliProfileId')) {
        // Check sessionStorage as last resort (legacy approach)
        hasProfileData = true
        profileId = sessionStorage.getItem('meliProfileId')
        console.log('Using profile from sessionStorage for MercadoLibre authentication')
      }

      // Validate user is logged in and has profile data
      if (!hasProfileData) {
        setErrorMessage('No hay una sesión activa o falta información del perfil. Por favor, inicie sesión nuevamente.')
        setProcessingStatus('error')

        return
      }

      if (code) {
        try {
          // Only pass profileId if it's not null
          const token = await meliExchangeCode(code, profileId || undefined)

          if (token) {
            // Authentication successful
            setProcessingStatus('success')

            // volvemos a donde nos llamaron
            const item = 'meliLoginRedirect'
            const redirectPath = sessionStorage.getItem(item) ?? '/'
            sessionStorage.removeItem(item)
            // Clear the stored profile ID since we're done with the OAuth flow
            sessionStorage.removeItem('meliProfileId')
            navigate(redirectPath) // 🎯 Back to where they were!
          } else {
            // The exchange failed but didn't throw an error
            setErrorMessage('No se pudo completar la autenticación con MercadoLibre. Por favor, intente nuevamente.')
            setProcessingStatus('error')
          }
        } catch (error) {
          setErrorMessage(
            `Error al procesar la autenticación: ${error instanceof Error ? error.message : 'Error desconocido'}`
          )
          setProcessingStatus('error')
        }
      } else {
        setErrorMessage('No se recibió código de autorización de MercadoLibre.')
        setProcessingStatus('error')
      }
    }
    fetchAsync()
  }, [])

  // Show appropriate message based on status
  return (
    <div className="meli-redirect-container">
      {processingStatus === 'processing' && (
        <>
          <div>
            <DisplayError msg={errorMeliExchangeCode?.message} />
          </div>
          <p>Conectando con Mercado Libre...</p>
        </>
      )}

      {processingStatus === 'error' && (
        <div className="error-container">
          <h3>Error de conexión con MercadoLibre</h3>
          <p>{errorMessage || errorMeliExchangeCode?.message || 'Error desconocido'}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Volver al inicio
          </button>
        </div>
      )}

      {processingStatus === 'success' && <p>Conexión exitosa con MercadoLibre. Redirigiendo...</p>}
    </div>
  )
}
export default MeliRedirected
